import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SelectOption, SnackbarService } from '@bcl/design-system';
import { TimesheetDialogComponent } from 'src/app/pages/visit-day/components/timesheet-dialog/timesheet-dialog.component';
import { RestartDayDialogComponent } from '../restart-day-dialog/restart-day-dialog.component';
import { GeolocationService } from 'src/app/services/geo-location.service';
import { TimesheetApiService } from 'src/app/services/timesheet.service';
import {
  AccordionService,
  AccordionItem,
} from 'src/app/services/accordion.service';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';

@Component({
  selector: 'app-visit-date',
  templateUrl: './visit-date.component.html',
  styleUrls: ['./visit-date.component.scss'],
})
export class VisitDateComponent implements OnInit, OnDestroy, OnChanges {
  @Input() dateObject: any = {};
  @Input() tabValue = '';
  @Input() timesheetLabels: SelectOption[] = [];
  @Input() accordionGroupId?: string;
  @Input() accordionId?: string;
  @Input() initiallyOpen = false;
  @Input() warehouseOptions: SelectOption[] = [];
  @Input() warehouseOptionsLoaded = false;
  @Input() warehouseOptionsRefreshing = false;
  @Output() dataRefresh = new EventEmitter<void>();

  accordionItem$: Observable<AccordionItem> | null = null;
  showItemList = false;

  dateStarted = false;
  dateEnded = false;

  googleMapsUrl = 'https://www.google.com/maps/dir/';
  public days: any[] = [
    {
      key: 'DAYS.FOURTEEN_DAYS_AGO',
      value: -14,
    },
    {
      key: 'DAYS.THIRTEEN_DAYS_AGO',
      value: -13,
    },
    {
      key: 'DAYS.TWELVE_DAYS_AGO',
      value: -12,
    },
    {
      key: 'DAYS.ELEVEN_DAYS_AGO',
      value: -11,
    },
    {
      key: 'DAYS.TEN_DAYS_AGO',
      value: -10,
    },
    {
      key: 'DAYS.NINE_DAYS_AGO',
      value: -9,
    },
    {
      key: 'DAYS.EIGHT_DAYS_AGO',
      value: -8,
    },
    {
      key: 'DAYS.SEVEN_DAYS_AGO',
      value: -7,
    },
    {
      key: 'DAYS.SIX_DAYS_AGO',
      value: -6,
    },
    {
      key: 'DAYS.FIVE_DAYS_AGO',
      value: -5,
    },
    {
      key: 'DAYS.FOUR_DAYS_AGO',
      value: -4,
    },
    {
      key: 'DAYS.THREE_DAYS_AGO',
      value: -3,
    },
    {
      key: 'DAYS.TWO_DAYS_AGO',
      value: -2,
    },
    {
      key: 'DAYS.YESTERDAY',
      value: -1,
    },
    {
      key: 'DAYS.TODAY',
      value: 0,
    },
    {
      key: 'DAYS.TOMORROW',
      value: 1,
    },
    {
      key: 'DAYS.TWO_DAYS',
      value: 2,
    },
    {
      key: 'DAYS.THREE_DAYS',
      value: 3,
    },
    {
      key: 'DAYS.FOUR_DAYS',
      value: 4,
    },
    {
      key: 'DAYS.FIVE_DAYS',
      value: 5,
    },
    {
      key: 'DAYS.SIX_DAYS',
      value: 6,
    },
    {
      key: 'DAYS.SEVEN_DAYS',
      value: 7,
    },
    {
      key: 'DAYS.EIGHT_DAYS',
      value: 8,
    },
    {
      key: 'DAYS.NINE_DAYS',
      value: 9,
    },
    {
      key: 'DAYS.TEN_DAYS',
      value: 10,
    },
    {
      key: 'DAYS.ELEVEN_DAYS',
      value: 11,
    },
    {
      key: 'DAYS.TWELVE_DAYS',
      value: 12,
    },
    {
      key: 'DAYS.THIRTEEN_DAYS',
      value: 13,
    },
    {
      key: 'DAYS.FOURTEEN_DAYS',
      value: 14,
    },
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private timesheetApiService: TimesheetApiService,
    private geoLocationService: GeolocationService,
    private accordionService: AccordionService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.dateObject && this.dateObject) {
      this.assignLetters();
      this.assignDayNumber();
    }
  }

  ngOnInit(): void {
    this.assignLetters();
    this.assignDayNumber();
    this.dateStarted = this.dateObject.objects[0]?.dayStarted;
    this.dateEnded = this.dateObject.objects[0]?.dayEnded;

    if (this.accordionId && this.accordionGroupId) {
      this.accordionItem$ = this.accordionService.registerItem(
        this.accordionId,
        this.accordionGroupId,
        this.initiallyOpen
      );

      this.accordionItem$.pipe(takeUntil(this.destroy$)).subscribe((item) => {
        this.showItemList = item.isOpen;
      });
    } else {
      this.showItemList = this.initiallyOpen;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.accordionId) {
      this.accordionService.unregisterItem(this.accordionId);
    }
  }

  showItems() {
    if (this.accordionId) {
      this.accordionService.toggleItem(this.accordionId);
    } else {
      this.showItemList = !this.showItemList;
    }
  }

  generateRouteLink() {
    let url = this.googleMapsUrl;
    this.dateObject.objects.forEach((visit) => {
      if (visit.locationLat && visit.locationLng) {
        url += `/${visit.locationLat},${visit.locationLng}`;
      }
    });

    window.open(encodeURI(url), '_system', 'location=no');
  }

  assignLetters() {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const objectsForLetters =
      this.dateObject.allObjects || this.dateObject.objects;

    objectsForLetters
      .filter((el) => !el.nonVisit)
      .forEach((obj, index) => {
        obj.letter = alphabet[index % alphabet.length];
      });
    objectsForLetters
      .filter((el) => el.nonVisit)
      .forEach((obj, index) => {
        obj.letter = 'NV';
      });
  }
  assignDayNumber() {
    const date1 = new Date(this.dateObject.date);

    const date2 = new Date(Date.now());
    date2.setHours(0, 0, 0, 0);

    const oneDay = 1000 * 60 * 60 * 24;

    const diffInTime = date1.getTime() - date2.getTime();

    const diffInDays = Math.round(diffInTime / oneDay);
    this.dateObject.dayNumber = diffInDays;
  }

  public showDay(status: string): string {
    const statusToShow = this.days.find((x) => x.value === status)?.key;

    if (typeof statusToShow === 'string') {
      return statusToShow;
    } else {
      return '';
    }
  }

  generateTimesheet(type: string) {
    this.openTimesheetDialog(type);
  }

  restartDay() {
    this.openRestartDayDialog();
  }

  private openRestartDayDialog() {
    const dialogRef = this.dialog.open(RestartDayDialogComponent, {
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.timesheetApiService.addTimesheet(998, null, null, null).subscribe({
          next: () => {
            this.dateEnded = false;
            this.dateObject.objects.forEach((job) => {
              job.dayEnded = false;
            });

            this.snackbarService.open(
              'green',
              'TIMESHEET_EDIT.MESSAGES.DAY_RESTARTED'
            );
            this.dataRefresh.emit();
          },
          error: () => {
            this.snackbarService.open(
              'red',
              'TIMESHEET_EDIT.MESSAGES.DAY_RESTART_ERROR'
            );
          },
        });
      }
    });
  }

  private openTimesheetDialog(type: string) {
    const dialogRef = this.dialog.open(TimesheetDialogComponent, {
      disableClose: true,
      data: {
        actionType: type,
        timesheetTypeOptions: this.timesheetLabels,
        warehouseOptions: this.warehouseOptions,
      },
    });
    this.handleTimesheetDialogResult(dialogRef);
  }

  private handleTimesheetDialogResult(dialogRef: any) {
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.timesheetApiService
          .addTimesheet(
            result.timesheetTypeId,
            null,
            result.timesheetCarMileage,
            result.warehouseId
          )
          .subscribe({
            next: () => {
              if (result.timesheetTypeId.toString() === '1') {
                this.dateObject.objects.forEach((job) => {
                  job.dayStarted = true;
                });
                this.dateStarted = true;
                this.geoLocationService.initializeGeoLocation();
              }

              if (result.timesheetTypeId.toString() === '2') {
                this.dateObject.objects.forEach((job) => {
                  job.dayEnded = true;
                });
                this.dateEnded = true;
                this.geoLocationService.cleanupAfterTurnOff();
              }
              this.snackbarService.open(
                'green',
                'TIMESHEET_EDIT.MESSAGES.TIMESHEET_CREATED'
              );
            },
            error: () => {
              this.snackbarService.open(
                'red',
                'TIMESHEET_EDIT.MESSAGES.TIMESHEET_CREATE_ERROR'
              );
            },
          });
      }
    });
  }
}
